import React, { useEffect, useState, useRef, useCallback } from 'react';
import { foldersApi, leadsApi } from '../api';
import { toast } from 'sonner';
import {
  Upload, Search, Trash2, CheckCircle, Loader2, Sparkles, ChevronLeft, ChevronRight,
  Users, FolderPlus, FolderOpen, Folder, MoreVertical, Plus, UserPlus, X,
  AlertTriangle, Pencil, Shield, Zap, Flame, Thermometer, Snowflake
} from 'lucide-react';

const STATUS_STYLES = {
  pending: { bg: 'bg-[#E8EAED]', text: 'text-[#5F6368]' },
  verified: { bg: 'bg-[#E6F4EA]', text: 'text-[#1E8E3E]' },
  invalid: { bg: 'bg-[#FCE8E6]', text: 'text-[#D93025]' },
  enriched: { bg: 'bg-[#E8F0FE]', text: 'text-[#1A73E8]' },
  sent: { bg: 'bg-[#E0F7FA]', text: 'text-[#0891B2]' },
  bounced: { bg: 'bg-[#FFF3E0]', text: 'text-[#EA580C]' },
  replied: { bg: 'bg-[#F3E8FF]', text: 'text-[#7C3AED]' },
};

const TAG_STYLES = {
  hot: { bg: 'bg-[#D93025]', text: 'text-white', icon: Flame, label: 'Hot' },
  warm: { bg: 'bg-[#F9AB00]', text: 'text-white', icon: Thermometer, label: 'Warm' },
  cold: { bg: 'bg-[#1A73E8]', text: 'text-white', icon: Snowflake, label: 'Cold' },
};

export default function Leads() {
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [tagFilter, setTagFilter] = useState('');
  const [tagDropdownId, setTagDropdownId] = useState(null);

  // Add lead modal
  const [showAddLead, setShowAddLead] = useState(false);
  const [newLead, setNewLead] = useState({ company_name: '', directors: '', email: '' });
  const [addingLead, setAddingLead] = useState(false);

  // Jobs
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);

  // Single lead actions
  const [verifyingId, setVerifyingId] = useState(null);
  const [enrichingId, setEnrichingId] = useState(null);

  const fetchFolders = async () => {
    try { setFolders(await foldersApi.getAll()); }
    catch { /* ignore */ }
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, search: search || undefined, status: status || undefined };
      if (activeFolder) params.folder_id = activeFolder;
      if (tagFilter) params.tag = tagFilter;
      const data = await leadsApi.getAll(params);
      setLeads(data.leads);
      setTotal(data.total);
    } catch { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  }, [page, search, status, activeFolder, tagFilter]);

  useEffect(() => { fetchFolders(); }, []);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(async () => {
      try {
        const j = await leadsApi.jobStatus(jobId);
        setJob(j);
        if (j.status === 'completed') {
          clearInterval(interval);
          setJobId(null); setJob(null);
          fetchLeads(); fetchFolders();
          if (j.error_message) {
            toast.error(j.error_message, { duration: 10000 });
          } else if (j.type === 'verification') {
            toast.success(`Verified: ${j.verified || 0} valid, ${j.invalid || 0} invalid`, { duration: 6000 });
          } else {
            toast.success(`Enriched: ${j.found || 0} emails found`, { duration: 6000 });
          }
        }
      } catch { clearInterval(interval); setJobId(null); setJob(null); }
    }, 2000);
    return () => clearInterval(interval);
  }, [jobId]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      const f = await foldersApi.create(newFolderName.trim());
      setNewFolderName('');
      fetchFolders();
      setActiveFolder(f.id);
      toast.success('Folder created');
    } catch { toast.error('Failed to create folder'); }
    finally { setCreatingFolder(false); }
  };

  const handleRenameFolder = async (id) => {
    if (!renameValue.trim()) return;
    try {
      await foldersApi.rename(id, renameValue.trim());
      setRenamingId(null);
      fetchFolders();
    } catch { toast.error('Rename failed'); }
  };

  const handleDeleteFolder = async (id) => {
    if (!window.confirm('Delete this folder and all its leads?')) return;
    try {
      await foldersApi.delete(id);
      if (activeFolder === id) setActiveFolder(null);
      fetchFolders(); fetchLeads();
      toast.success('Folder deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!activeFolder) { toast.error('Please select a folder first'); return; }
    setUploading(true);
    try {
      const res = await foldersApi.uploadLeads(activeFolder, file);
      toast.success(res.message);
      fetchLeads(); fetchFolders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleAddLead = async () => {
    if (!activeFolder) { toast.error('Please select a folder first'); return; }
    if (!newLead.company_name.trim() && !newLead.email.trim()) { toast.error('Company name or email required'); return; }
    setAddingLead(true);
    try {
      await foldersApi.addLead(activeFolder, newLead);
      toast.success('Lead added');
      setNewLead({ company_name: '', directors: '', email: '' });
      setShowAddLead(false);
      fetchLeads(); fetchFolders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add lead'); }
    finally { setAddingLead(false); }
  };

  const handleVerifySingle = async (leadId) => {
    setVerifyingId(leadId);
    try {
      const res = await leadsApi.verifySingle(leadId);
      toast.success(`${res.email}: ${res.status} (${res.result})`);
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Verification failed'); }
    finally { setVerifyingId(null); }
  };

  const handleEnrichSingle = async (leadId) => {
    setEnrichingId(leadId);
    try {
      const res = await leadsApi.enrichSingle(leadId);
      if (res.found) toast.success(`Email found: ${res.email}`);
      else toast.info(res.message);
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Enrichment failed'); }
    finally { setEnrichingId(null); }
  };

  const handleBulkVerify = async () => {
    try {
      const res = await leadsApi.verify(activeFolder);
      if (res.total === 0) { toast.info('No leads to verify'); return; }
      setJobId(res.job_id);
      toast.info(`Verifying ${res.total} emails...`, { duration: 4000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleBulkEnrich = async () => {
    try {
      const res = await leadsApi.enrich(activeFolder);
      if (res.total === 0) { toast.info('All leads already have emails'); return; }
      setJobId(res.job_id);
      toast.info(`Enriching ${res.total} leads...`, { duration: 4000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteLead = async (id) => {
    try { await leadsApi.delete(id); fetchLeads(); fetchFolders(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleTagLead = async (id, tag) => {
    try {
      await leadsApi.tag(id, tag);
      setTagDropdownId(null);
      fetchLeads();
    } catch { toast.error('Failed to tag lead'); }
  };

  useEffect(() => {
    if (!tagDropdownId) return;
    const close = () => setTagDropdownId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [tagDropdownId]);

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="flex gap-5" data-testid="outreach-leads">
      {/* Folder Sidebar */}
      <div className="w-[220px] flex-shrink-0">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#F1F3F4]">
            <h3 className="text-sm font-semibold text-[#202124] flex items-center gap-1.5"><FolderOpen className="w-4 h-4 text-[#1A73E8]" /> Folders</h3>
          </div>
          {/* Create folder */}
          <div className="px-3 py-2 border-b border-[#F1F3F4]">
            <div className="flex gap-1.5">
              <input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                placeholder="New folder..." className="flex-1 bg-[#F1F3F4] rounded-lg px-2.5 py-1.5 text-xs text-[#202124] placeholder:text-[#9AA0A6] focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#1A73E8] min-w-0" data-testid="new-folder-input" />
              <button onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}
                className="p-1.5 bg-[#1A73E8] text-white rounded-lg hover:bg-[#174EA6] disabled:opacity-50 flex-shrink-0" data-testid="create-folder-btn">
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          {/* All leads */}
          <button onClick={() => { setActiveFolder(null); setPage(1); }}
            className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${
              !activeFolder ? 'bg-[#D3E3FD] text-[#041E49] font-semibold' : 'text-[#5F6368] hover:bg-[#F8F9FA]'
            }`} data-testid="all-leads-btn">
            <Users className="w-4 h-4" /> All Leads
          </button>
          {/* Folder list */}
          <div className="max-h-[400px] overflow-y-auto">
            {folders.map(f => (
              <div key={f.id} className={`group flex items-center justify-between px-4 py-2 cursor-pointer transition-colors ${
                activeFolder === f.id ? 'bg-[#D3E3FD] text-[#041E49]' : 'text-[#5F6368] hover:bg-[#F8F9FA]'
              }`}>
                {renamingId === f.id ? (
                  <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFolder(f.id); if (e.key === 'Escape') setRenamingId(null); }}
                    onBlur={() => handleRenameFolder(f.id)} autoFocus
                    className="flex-1 bg-white border border-[#1A73E8] rounded px-2 py-0.5 text-xs mr-1" />
                ) : (
                  <button onClick={() => { setActiveFolder(f.id); setPage(1); }}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left">
                    <Folder className={`w-4 h-4 flex-shrink-0 ${activeFolder === f.id ? 'text-[#1A73E8]' : ''}`} />
                    <span className="text-sm truncate">{f.name}</span>
                    <span className="text-[10px] text-[#9AA0A6] font-mono ml-auto flex-shrink-0">{f.lead_count}</span>
                  </button>
                )}
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 ml-1">
                  <button onClick={() => { setRenamingId(f.id); setRenameValue(f.name); }} className="p-0.5 text-[#9AA0A6] hover:text-[#1A73E8]"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => handleDeleteFolder(f.id)} className="p-0.5 text-[#9AA0A6] hover:text-[#D93025]"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-[#202124]">
              {activeFolder ? folders.find(f => f.id === activeFolder)?.name || 'Folder' : 'All Leads'}
            </h1>
            <p className="text-sm text-[#5F6368]">{total} contacts</p>
          </div>
          <div className="flex items-center gap-2">
            {activeFolder && (
              <>
                <input type="file" ref={fileRef} accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-xs font-medium disabled:opacity-50 transition-colors shadow-sm" data-testid="upload-leads-btn">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload Excel
                </button>
                <button onClick={() => setShowAddLead(true)}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#202124] rounded-full text-xs font-medium transition-colors shadow-sm" data-testid="add-lead-btn">
                  <UserPlus className="w-3.5 h-3.5 text-[#1A73E8]" /> Add Lead
                </button>
              </>
            )}
            <button onClick={handleBulkVerify} disabled={!!jobId}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#202124] rounded-full text-xs font-medium disabled:opacity-50 transition-colors shadow-sm" data-testid="verify-btn">
              <CheckCircle className="w-3.5 h-3.5 text-[#1E8E3E]" /> Verify All
            </button>
            <button onClick={handleBulkEnrich} disabled={!!jobId}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#202124] rounded-full text-xs font-medium disabled:opacity-50 transition-colors shadow-sm" data-testid="enrich-btn">
              <Sparkles className="w-3.5 h-3.5 text-[#7C3AED]" /> Enrich
            </button>
          </div>
        </div>

        {/* No folder selected */}
        {!activeFolder && folders.length === 0 && !loading && leads.length === 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-16 text-center">
            <FolderPlus className="w-12 h-12 mx-auto mb-4 text-[#9AA0A6]" />
            <p className="text-lg font-semibold text-[#202124] mb-1">Create your first folder</p>
            <p className="text-sm text-[#5F6368]">Organize your leads into folders, then upload Excel files or add leads one by one</p>
          </div>
        )}

        {/* Job progress */}
        {job && (
          <div className={`${job.error_message ? 'bg-[#FCE8E6] border-[#D93025]/30' : 'bg-[#E8F0FE] border-[#D3E3FD]'} border rounded-xl p-3 mb-4 flex items-center gap-3`}>
            {job.error_message ? <AlertTriangle className="w-4 h-4 text-[#D93025]" /> : <Loader2 className="w-4 h-4 animate-spin text-[#1A73E8]" />}
            <div className="flex-1">
              <p className={`text-sm font-medium ${job.error_message ? 'text-[#D93025]' : 'text-[#041E49]'}`}>
                {job.error_message || (job.type === 'verification' ? 'Verifying Emails' : 'Enriching Leads')}
              </p>
              <div className="w-full bg-[#D3E3FD] rounded-full h-1.5 mt-1">
                <div className={`${job.error_message ? 'bg-[#D93025]' : 'bg-[#1A73E8]'} h-1.5 rounded-full transition-all`} style={{ width: `${job.total ? (job.processed / job.total) * 100 : 0}%` }} />
              </div>
            </div>
            <span className="text-sm font-mono font-semibold text-[#1A73E8]">{job.processed}/{job.total}</span>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 text-[#9AA0A6] absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search leads..." className="w-full bg-[#F1F3F4] border border-transparent rounded-full pl-9 pr-3 py-2 text-sm text-[#202124] placeholder:text-[#9AA0A6] focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20" data-testid="search-leads" />
          </div>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="bg-white border border-[#E2E8F0] rounded-full px-4 py-2 text-sm text-[#5F6368] focus:outline-none focus:border-[#1A73E8] cursor-pointer" data-testid="filter-status">
            <option value="">All Status</option>
            {['pending','verified','invalid','enriched','sent','bounced','replied'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={tagFilter} onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
            className="bg-white border border-[#E2E8F0] rounded-full px-4 py-2 text-sm text-[#5F6368] focus:outline-none focus:border-[#1A73E8] cursor-pointer" data-testid="filter-tag">
            <option value="">All Tags</option>
            <option value="hot">Hot</option>
            <option value="warm">Warm</option>
            <option value="cold">Cold</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#F1F3F4] text-left">
                {['Company', 'Director/Person', 'Email', 'Status', 'Tag', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-[#5F6368] uppercase tracking-wide">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#1A73E8]" /></td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-16 text-center">
                    <Users className="w-10 h-10 mx-auto mb-3 text-[#9AA0A6]" />
                    <p className="text-[#5F6368] font-medium">{activeFolder ? 'No leads in this folder' : 'No leads found'}</p>
                    {activeFolder && <p className="text-sm text-[#9AA0A6] mt-1">Upload an Excel file or add leads individually</p>}
                  </td></tr>
                ) : leads.map((lead) => {
                  const s = STATUS_STYLES[lead.status] || STATUS_STYLES.pending;
                  return (
                    <tr key={lead.id} className="border-b border-[#F1F3F4] hover:bg-[#F8F9FA] transition-colors group">
                      <td className="px-4 py-2.5 text-[#202124] font-medium max-w-[200px] truncate">{lead.company_name}</td>
                      <td className="px-4 py-2.5 text-[#5F6368] max-w-[180px] truncate">{lead.directors}</td>
                      <td className="px-4 py-2.5 text-[#5F6368] max-w-[220px] truncate">{lead.email || <span className="text-[#9AA0A6] italic">no email</span>}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{lead.status}</span>
                      </td>
                      <td className="px-4 py-2.5 relative">
                        {lead.tag && TAG_STYLES[lead.tag] ? (
                          <button onClick={(e) => { e.stopPropagation(); setTagDropdownId(tagDropdownId === lead.id ? null : lead.id); }}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${TAG_STYLES[lead.tag].bg} ${TAG_STYLES[lead.tag].text}`}
                            data-testid={`tag-badge-${lead.id}`}>
                            {React.createElement(TAG_STYLES[lead.tag].icon, { className: 'w-3 h-3' })}
                            {TAG_STYLES[lead.tag].label}
                          </button>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setTagDropdownId(tagDropdownId === lead.id ? null : lead.id); }}
                            className="px-2 py-0.5 rounded-full text-xs font-medium text-[#9AA0A6] border border-dashed border-[#E2E8F0] hover:border-[#9AA0A6] hover:text-[#5F6368] transition-colors"
                            data-testid={`tag-btn-${lead.id}`}>
                            + Tag
                          </button>
                        )}
                        {tagDropdownId === lead.id && (
                          <div className="absolute z-20 mt-1 bg-white border border-[#E2E8F0] rounded-xl shadow-lg py-1 w-28" onClick={(e) => e.stopPropagation()} data-testid={`tag-dropdown-${lead.id}`}>
                            {[
                              { value: 'hot', label: 'Hot', icon: Flame, color: 'text-[#D93025]' },
                              { value: 'warm', label: 'Warm', icon: Thermometer, color: 'text-[#F9AB00]' },
                              { value: 'cold', label: 'Cold', icon: Snowflake, color: 'text-[#1A73E8]' },
                              { value: '', label: 'Clear', icon: X, color: 'text-[#9AA0A6]' },
                            ].map(t => (
                              <button key={t.value} onClick={(e) => { e.stopPropagation(); handleTagLead(lead.id, t.value); }}
                                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs font-medium ${t.color} hover:bg-[#F8F9FA] transition-colors`}>
                                {React.createElement(t.icon, { className: 'w-3.5 h-3.5' })} {t.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          {lead.email && (
                            <button onClick={() => handleVerifySingle(lead.id)} disabled={verifyingId === lead.id} title="Verify email"
                              className="p-1.5 text-[#5F6368] hover:text-[#1E8E3E] hover:bg-[#E6F4EA] rounded-full">
                              {verifyingId === lead.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {!lead.email && (
                            <button onClick={() => handleEnrichSingle(lead.id)} disabled={enrichingId === lead.id} title="Find email"
                              className="p-1.5 text-[#5F6368] hover:text-[#7C3AED] hover:bg-[#F3E8FF] rounded-full">
                              {enrichingId === lead.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          <button onClick={() => handleDeleteLead(lead.id)} title="Delete"
                            className="p-1.5 text-[#9AA0A6] hover:text-[#D93025] hover:bg-[#FCE8E6] rounded-full">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#F1F3F4]">
              <span className="text-xs text-[#5F6368]">Page {page} of {totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-full text-[#5F6368] hover:bg-[#F1F3F4] disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-full text-[#5F6368] hover:bg-[#F1F3F4] disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Lead Modal */}
      {showAddLead && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowAddLead(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F1F3F4]">
              <h3 className="text-base font-semibold text-[#202124] flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#1A73E8]" /> Add Lead</h3>
              <button onClick={() => setShowAddLead(false)} className="p-1 hover:bg-[#F1F3F4] rounded-full"><X className="w-4 h-4 text-[#5F6368]" /></button>
            </div>
            <div className="p-6 space-y-3">
              {[
                { key: 'company_name', label: 'Company Name', placeholder: 'Acme Corp' },
                { key: 'directors', label: 'Director / Person Name', placeholder: 'John Doe' },
                { key: 'email', label: 'Email Id', placeholder: 'john@acme.com' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs text-[#5F6368] font-medium block mb-1">{f.label}</label>
                  <input value={newLead[f.key]} onChange={(e) => setNewLead(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder} className="w-full bg-[#F1F3F4] border border-transparent rounded-lg px-3 py-2 text-sm text-[#202124] focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20"
                    data-testid={`add-lead-${f.key}`} />
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-[#F1F3F4] flex justify-end gap-2">
              <button onClick={() => setShowAddLead(false)} className="px-4 py-2 text-sm text-[#5F6368] hover:bg-[#F1F3F4] rounded-full">Cancel</button>
              <button onClick={handleAddLead} disabled={addingLead}
                className="flex items-center gap-1.5 px-5 py-2 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-sm font-medium disabled:opacity-50 shadow-sm" data-testid="confirm-add-lead-btn">
                {addingLead ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
